import { useState, useEffect, useRef, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { isNetworkError } from '../utils/errorHandler';

interface AsyncState<T> {
  data: T | null;
  isLoading: boolean;
  error: string | null;
  isNetworkError: boolean;
}

interface AsyncResult<T> extends AsyncState<T> {
  refetch: () => void;
}

export function useAsyncData<T>(
  fetcher: () => Promise<T>,
  deps: React.DependencyList
): AsyncResult<T> {
  const [state, setState] = useState<AsyncState<T>>({
    data: null,
    isLoading: true,
    error: null,
    isNetworkError: false,
  });
  const cancelledRef = useRef(false);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const execute = useCallback(() => {
    cancelledRef.current = false;
    setState(prev => ({ ...prev, isLoading: true, error: null, isNetworkError: false }));

    fetcherRef.current()
      .then(data => {
        if (!cancelledRef.current) {
          setState({ data, isLoading: false, error: null, isNetworkError: false });
        }
      })
      .catch(err => {
        const networkErr = isNetworkError(err);
        if (!networkErr) {
          Sentry.captureException(err);
        }
        if (!cancelledRef.current) {
          setState({ data: null, isLoading: false, error: err?.message || 'An error occurred', isNetworkError: networkErr });
        }
      });
  }, []);

  useEffect(() => {
    execute();
    return () => {
      cancelledRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { ...state, refetch: execute };
}
